import { motion } from 'framer-motion'
import { useState, useRef, useEffect } from 'react'
import type { Donation } from '../../api/giving'
import { formatTotalRaised, formatDonorName } from '../../utils/format'

interface DonationCardProps {
  donation: Donation
}

const DonationCard = ({ donation }: DonationCardProps) => {
  const { display_name, donation_amount, donation_msg } = donation
  const name = formatDonorName(display_name)

  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (textRef.current) {
      // Check if text is overflowing (line-clamp-3)
      setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight)
    }
  }, [donation_msg])

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="bg-gray-100 rounded-lg shadow-sm border-2 border-[#87cefa] pl-4 pr-4 py-2 flex flex-col gap-2 hover:shadow-md transition-shadow duration-200 relative"
    >      

      <div className="flex items-center justify-between gap-3 mr-2">
        <span className="font-medium text-red-500 text-1xl font-[FbAsli] leading-snug">
          {name}
        </span>
        <span className="text-red-500 text-1xl font-bold font-[Almoni] whitespace-nowrap">
          {formatTotalRaised(donation_amount)}
        </span>
      </div>

      {donation_msg && (
        <div className="mr-2 relative pb-2 bg-white rounded-sm p-2">
          <div className="absolute p-3 rotate-25 text-center flex items-center justify-center right-[-20px] w-12 h-12 bg-[#e62e2e] rounded-full shadow-md border-1 border-white font-[Almoni] text-white">
        <div className="flex flex-col leading-none items-center pb-1">
           <span className="text-[10px] font-medium font-[FbAsli]"> הרגע שלי עם לאה׳לע</span>
        </div>
      </div>
          <p 
            ref={textRef}
            className={`text-[#6b7280] text-base leading-none transition-all duration-300 pr-7 ${expanded ? '' : 'line-clamp-3'}`}
            style={{ 
              display: expanded ? 'block' : '-webkit-box',
              WebkitLineClamp: expanded ? 'unset' : 3,
              WebkitBoxOrient: 'vertical',
              overflow: expanded ? 'visible' : 'hidden',
              fontFamily: 'Almoni, Arial Hebrew, sans-serif'
            }}
          >
            {donation_msg}
          </p>
          
          {/* Small red down-left arrow for expansion toggle */}
          {(isOverflowing || expanded) && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="absolute bottom-0 left-0 p-1 text-[#e62e2e] hover:text-[#c52828] transition-colors focus:outline-none"
              aria-label={expanded ? "הסתר" : "הצג עוד"}
            >
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className={`transform transition-transform duration-300 ${expanded ? 'rotate-90' : '-rotate-45'}`}
              >
                <path d="M19 12H5M5 12L12 19M5 12L12 5" />
              </svg>
            </button>
          )}
        </div>
      )}
    </motion.div>
  )
}

export default DonationCard
